import React from 'react';
import { useStripe } from '@stripe/react-stripe-js';

import { useSwitchPlansMutation } from '$/GraphQL/schema/schema';

import { useOnSubscriptionChanged } from '$/hooks/useOnSubscriptionChanged';
import { logger } from '$/utils/logger';

export const useSwitchPlans = (): {
  submitting: boolean;
  error?: string;
  switchPlans: () => Promise<void>;
} => {
  const stripe = useStripe();
  const [submitting, setSubmitting] = React.useState(false);
  const [switchPlansMutation] = useSwitchPlansMutation();
  const [error, setError] = React.useState('');
  const onSubscriptionChanged = useOnSubscriptionChanged();

  const switchPlans = React.useCallback(async (): Promise<void> => {
    try {
      setSubmitting(true);
      setError('');
      const switchPlansResult = await switchPlansMutation();
      const paymentIntentSecret = switchPlansResult.data?.switchPlans.paymentIntentSecret;
      if (paymentIntentSecret) {
        if (!stripe) {
          setError(
            `We are unable to authenticate your payment method. Please choose a different payment method and try again.  Please retry in a few seconds.`
          );
          return;
        }
        const paymentIntent = await stripe?.confirmCardPayment(paymentIntentSecret);
        await switchPlansMutation({
          variables: {
            paymentIntentId:
              paymentIntent.error?.payment_intent?.id ?? paymentIntent?.paymentIntent?.id,
          },
        });
        if (paymentIntent.error) {
          setError(
            `We are unable to authenticate your payment method. Please choose a different payment method and try again.  Please retry in a few seconds.`
          );
          return;
        }
      }
      await onSubscriptionChanged();
    } catch (e) {
      logger.error('Unable to switch plans', { error: e });
      setError(
        `We are unable to authenticate your payment method. Please choose a different payment method and try again.  Please retry in a few seconds.`
      );
    } finally {
      setSubmitting(false);
    }
  }, [onSubscriptionChanged, stripe, switchPlansMutation]);
  return {
    error,
    submitting,
    switchPlans,
  };
};
